import { useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";

/**
 * In-app notification fallback.
 *
 * Even when web-push works, iOS Safari and Android Chrome will *silently*
 * drop PWA push subscriptions after inactivity, OS updates, storage
 * cleanup, etc. Users then miss booking updates until they manually
 * re-toggle notifications.
 *
 * This hook is a belt-and-suspenders in-app channel:
 *   - Polls the user's active/upcoming bookings every 20 seconds while the
 *     app tab is visible.
 *   - Compares each booking's status against the last snapshot in memory.
 *   - When a status changes to something notification-worthy, we surface a
 *     visible in-app toast AND play a short chime. So even a stone-dead
 *     push subscription can't cause the operator or the rider to miss the
 *     event as long as the app is open.
 *
 * Stops polling when the tab is hidden (saves battery) and resumes on
 * visibility-change.
 */

// Short, embedded chime (data URI so no extra asset load). Two-tone bell.
const CHIME_DATA_URI =
  "data:audio/wav;base64,UklGRoQCAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YWACAAAAABkFOAdSC1oPTBPXFcQXfxhIF7YUyxAcDMEG5AC9+p/0uu4/6VbkR+Bx3TnbzNls2QLal9tk3sTh3+YV7WT0mvxwBboOMhezH4Yn6y4kNJI4jTsuPTA9BjttNqAvISfNGwUPGgHF8QLiBdMrxOu0IqSekwiFRnjwbFRlSGCTX9BjeGtDdid1uV+HRHY0mSb2GT4NrgHy9jXsUeIN2Vfg9Ohn9DsBqQ5+HAErEjfjRfNTIWA4bBB2unwhg0eKY46/kBmS35EskGqOMoxxinCJLIiJhrKEXYNZgpiCXoQqhz2LTZBSlYCanaAMqCewO7iBv+HGiM6P1WHbwuBP5cbop+u+7hnzePeq+of8QP1v/kj/BgAoAJf/qP7t/YT9uv0X/w0BjgLIA6gEsQVSB2AJoAv/DCwOWA9GEBIRvxHqEUwR3RCcELEQ/xC/EbISvBOwFOgVBhcQGE0YoBcXFxAXwhcaGeAaFRxOHFocdxwCHUseDR/tHnIeXR7bHmwfvB+/H8Uf7B8xIH8gjyBrIF0gVSAmIMQfyR/vHzUgRSAAIEIfhx7VHVsdMx0iHTgdSx0DHXscHxysG68euh4NHmYdQx1cHXwddx07HRcdBB3iHNAcyxwzHXId4B0zHiEeQx6PHrceOh5cHncebR52HmMeIB7NHZcdlx1uHRUdHR0dHRQdIB35HHUcIRygG34bMhvcGocaSBoyGh4a5xnUGa4ZuxnnGRQaOxpTGkkaHxq/GS8ZjhjYFywXhBbaFRoVWRSaE9YSFhKQEfEQNhBrD5AOrw3ADP4LNwtSCqQJ2wgWCFMHigbSBRQFVwSuAxsDkQIYAqcBRAHvAJsAWQBEACAAJAAyAF8AbQCLAKUAoQCzAJoAmQCUAJIAggBqAF4AVQBHACcAEQAAAAA=";

let sharedChimeAudio = null;
function playChime() {
  try {
    if (!sharedChimeAudio) {
      sharedChimeAudio = new Audio(CHIME_DATA_URI);
      sharedChimeAudio.volume = 0.35;
    }
    sharedChimeAudio.currentTime = 0;
    sharedChimeAudio.play().catch(() => { /* autoplay blocked — ignore */ });
  } catch { /* ignore */ }
}

const STATUS_ANNOUNCEMENTS = {
  confirmed: { title: "Driver confirmed your ride", tone: "info" },
  driver_on_the_way: { title: "Driver is on the way", tone: "info" },
  arrived: { title: "Driver has arrived", tone: "success" },
  in_progress: { title: "Trip started", tone: "info" },
  completed: { title: "Trip completed", tone: "success" },
  cancelled: { title: "Trip cancelled", tone: "error" },
};

export default function useInAppBookingAlerts() {
  const { user, token } = useAuth();
  const seenRef = useRef(new Map()); // bookingId → last status seen

  useEffect(() => {
    if (!user || !token) return;
    let cancelled = false;
    let interval = null;

    const check = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        // Customers get their own bookings; drivers/admins get everything
        // that's active. Both endpoints return the same schema.
        const endpoint =
          user.role === "driver" || user.role === "admin"
            ? `${API}/bookings`
            : `${API}/me/bookings`;
        const { data } = await axios.get(endpoint, {
          params: { active: 1 },
        });
        // /api/bookings returns {items:[...]}, /me/bookings returns [...]
        const rows = Array.isArray(data) ? data : (data?.items || data?.bookings || []);
        for (const b of rows) {
          if (!b || !b.id) continue;
          const prev = seenRef.current.get(b.id);
          const cur = b.status;
          seenRef.current.set(b.id, cur);
          // First observation — don't fire a toast (would spam on load)
          if (prev === undefined) continue;
          if (prev === cur) continue;
          const ann = STATUS_ANNOUNCEMENTS[cur];
          if (!ann) continue;
          const label =
            b.customer_name || b.pickup_address || `Booking ${b.id.slice(0, 6)}`;
          const msg = `${ann.title} · ${label}`;
          if (ann.tone === "success") toast.success(msg, { duration: 10000 });
          else if (ann.tone === "error") toast.error(msg, { duration: 10000 });
          else toast(msg, { duration: 10000 });
          playChime();
        }
      } catch { /* offline / auth failure — silent */ }
    };

    // Immediate check + interval, only while tab visible
    check();
    interval = setInterval(() => {
      if (cancelled) return;
      check();
    }, 20 * 1000);

    const onVis = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user, token]);
}
